import {IssueState, IssueStateReason} from '@github-ui/issue-pr-state/constants'
import {useMemo} from 'react'
import {graphql, useSubscription} from 'react-relay'
import type {GraphQLSubscriptionConfig} from 'relay-runtime'

import type {SimilarIssueUpdate} from '../hooks/use-similar-issues'
import type {SimilarIssue} from '../SimilarIssues'
import type {similarIssueSubscription} from './__generated__/similarIssueSubscription.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const subscription = graphql`
  subscription similarIssueSubscription($issueId: ID!) {
    issueUpdated(id: $issueId) {
      issueTitleUpdated {
        id
        title
      }
      issueStateUpdated {
        id
        state
        stateReason
      }
    }
  }
`

/** Type-safe conversion of GraphQL state to local type */
function toIssueState(state: string | null | undefined): SimilarIssue['state'] | undefined {
  if (state && state in IssueState) {
    return state as SimilarIssue['state']
  }
  if (state != null && process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn(`[SimilarIssueSubscription] Unexpected issue state: ${state}`)
  }
  return undefined
}

/** Type-safe conversion of GraphQL stateReason to local type */
function toIssueStateReason(stateReason: string | null | undefined): SimilarIssue['stateReason'] | undefined {
  // null/undefined are valid values (issue may not have a state reason)
  if (stateReason == null) {
    return stateReason
  }
  if (stateReason in IssueStateReason) {
    return stateReason as SimilarIssue['stateReason']
  }
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn(`[SimilarIssueSubscription] Unexpected issue stateReason: ${stateReason}`)
  }
  return undefined
}

interface UseSimilarIssueSubscriptionParams {
  /**
   * Called when the issue is updated via subscription.
   * IMPORTANT: This callback should be stable (wrapped in useCallback) to avoid
   * unnecessary subscription re-creation on every render.
   */
  onUpdate: (update: SimilarIssueUpdate) => void
}

/**
 * Subscribe to updates for a similar issue. When the issue's title or state changes,
 * the onUpdate callback is called with the new values.
 *
 * This hook should only be called when issueNodeId is defined. Use the
 * SimilarIssueSubscription component pattern to conditionally render subscriptions.
 *
 * @param issueNodeId - GraphQL node ID of the issue to subscribe to (required)
 * @param params.onUpdate - Callback for updates. Should be stable (memoized with useCallback)
 */
export const useSimilarIssueSubscription = (issueNodeId: string, {onUpdate}: UseSimilarIssueSubscriptionParams) => {
  const config = useMemo<GraphQLSubscriptionConfig<similarIssueSubscription>>(
    () => ({
      variables: {issueId: issueNodeId},
      subscription,
      updater: (_store, data) => {
        if (!data?.issueUpdated) return

        const {issueTitleUpdated, issueStateUpdated} = data.issueUpdated
        if (issueTitleUpdated || issueStateUpdated) {
          onUpdate({
            nodeId: issueNodeId,
            title: issueTitleUpdated?.title,
            state: toIssueState(issueStateUpdated?.state),
            stateReason: toIssueStateReason(issueStateUpdated?.stateReason),
          })
        }
      },
    }),
    [issueNodeId, onUpdate],
  )

  useSubscription(config)
}
